/**
 * Axios instance with:
 *  - baseURL from VITE_API_BASE_URL
 *  - request interceptor: attach access token from memory (AuthContext)
 *  - response interceptor: on 401 or 403 (except TENANT_WRITE_BLOCKED) → refresh ONCE → retry; if refresh fails → logout
 *
 * Backend returns 403 when JWT is expired/invalid (filter leaves context unauthenticated), so we treat 403 like 401 for refresh.
 * The refresh logic uses a queue to avoid multiple parallel refreshes.
 */

import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import type { RefreshResponse } from './types';
import { signalWriteBlocked } from '../context/BillingContext';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export const http = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
});

// ── Token accessors ──────────────────────────────────────────────────────────
// Stored in module memory (never in storage directly); populated by AuthContext.

let _accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  _accessToken = token;
}

export function getAccessToken(): string | null {
  return _accessToken;
}

// ── Refresh queue ─────────────────────────────────────────────────────────────

let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(token: string | null, error: unknown): void {
  pendingQueue.forEach((p) => {
    if (token) p.resolve(token);
    else p.reject(error);
  });
  pendingQueue = [];
}

// ── Refresh + logout callbacks (set by AuthContext) ──────────────────────────

let _onRefresh: (() => Promise<string>) | null = null;
let _onLogout: (() => void) | null = null;

export function registerHttpCallbacks(
  onRefresh: () => Promise<string>,
  onLogout: () => void,
): void {
  _onRefresh = onRefresh;
  _onLogout = onLogout;
}

// R0.3 Option B: show explicit message when 403 TENANT_WRITE_BLOCKED (called from app bridge)
let _onWriteBlockedMessage: (() => void) | null = null;

export function registerWriteBlockedMessageCallback(cb: () => void): void {
  _onWriteBlockedMessage = cb;
}

// ── Request interceptor: attach access token ─────────────────────────────────

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (_accessToken) {
    config.headers['Authorization'] = `Bearer ${_accessToken}`;
  }
  return config;
});

// ── Response interceptor: refresh-once on 401 or 403 (session expired) ────────

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retried?: boolean };

    // R0.3: reactive write-block — set global billing state + Option B: explicit message
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'TENANT_WRITE_BLOCKED'
    ) {
      signalWriteBlocked();
      _onWriteBlockedMessage?.();
      return Promise.reject(error);
    }

    // 401 = Unauthorized; 403 = Forbidden — backend returns 403 when JWT expired (unauthenticated)
    const isSessionExpired =
      error.response?.status === 401 ||
      (error.response?.status === 403 && error.response?.data?.code !== 'TENANT_WRITE_BLOCKED');
    const isAuthEndpoint =
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/refresh');

    if (!isSessionExpired || originalRequest._retried || isAuthEndpoint || !_onRefresh) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue subsequent calls while refresh is in flight
      return new Promise<string>((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      }).then((newToken) => {
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newToken}`,
        };
        return http(originalRequest);
      });
    }

    originalRequest._retried = true;
    isRefreshing = true;

    try {
      const newToken = await _onRefresh();
      processQueue(newToken, null);
      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${newToken}`,
      };
      return http(originalRequest);
    } catch (refreshError) {
      processQueue(null, refreshError);
      _onLogout?.();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);
